import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { journalEntryApi } from '../services/accountingApi';
import { JournalEntryCreate, JournalEntryReverse, JournalEntryUpdate, ListJournalEntriesParams } from '../types';
import { ACCOUNTS_QUERY_KEY } from './useAccounts';

export const JOURNAL_ENTRIES_QUERY_KEY = 'journal-entries';

export function useJournalEntries(params?: ListJournalEntriesParams) {
  return useQuery({
    queryKey: [JOURNAL_ENTRIES_QUERY_KEY, params],
    queryFn: () => journalEntryApi.list(params),
    placeholderData: keepPreviousData,
  });
}

export function useJournalEntry(id: string) {
  return useQuery({
    queryKey: [JOURNAL_ENTRIES_QUERY_KEY, id],
    queryFn: () => journalEntryApi.get(id),
    enabled: !!id,
  });
}

export function useCreateJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: JournalEntryCreate) => journalEntryApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOURNAL_ENTRIES_QUERY_KEY] });
    },
  });
}

export function useUpdateJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: JournalEntryUpdate }) =>
      journalEntryApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOURNAL_ENTRIES_QUERY_KEY] });
    },
  });
}

export function useDeleteJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => journalEntryApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOURNAL_ENTRIES_QUERY_KEY] });
    },
  });
}

export function usePostJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => journalEntryApi.post(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOURNAL_ENTRIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useReverseJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: JournalEntryReverse }) =>
      journalEntryApi.reverse(id, data),
    onSuccess: () => {
      // The reversal posts a new entry AND back-links the original, so both the
      // JE list/detail and every account balance touched by the pair change.
      queryClient.invalidateQueries({ queryKey: [JOURNAL_ENTRIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

